<template>
  <div class="lifecycle-container clearfix">
    <div
      class="reveal lifecycle-modal"
      ref="lifecycleModal"
      :id="modalId"
      data-reveal
    >
      <div
        v-if="histories.length"
        class="flex-box"
      >
        <div class="flex-box-content">
          <p class="confirm-header">{{ title }}</p>
          <table>
            <thead>
              <tr>
                <th
                  class="lifecycle-modal__status"
                  v-if="showStatusCol"
                >{{ $t('status.name') }}</th>
                <th
                  v-if="showCommentCol"
                  class="lifecycle-modal__comment"
                >{{ $t('comment') }}</th>
                <th class="lifecycle-modal__username">{{ $t('username') }}</th>
                <th class="lifecycle-modal__date">{{ $t('date') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(history, index) in orderedItems"
                :key="index"
              >
                <td v-if="showStatusCol">
                  <span v-if="type === 'directory'">
                    <span v-if="rowStatus(history) === DIR_STATUS.CART && history.action === DIR_ACTION.ADD">
                      {{ (`${$t('status.new').toUpperCase()} (${history.action})`) }}<i class="fa fa-shopping-cart cart-icon" />
                    </span>
                    <span v-else-if="rowStatus(history) === DIR_STATUS.CART && history.action === DIR_ACTION.REMOVE">
                      {{ (`${DIR_STATUS.PUBLISHED} (${history.action})`) }}<i class="fa fa-shopping-cart cart-icon" />
                    </span>
                    <span v-else>{{ `${rowStatus(history) || ''} ${history.action ? `(${history.action})` : ''}` }}</span>
                  </span>
                  <span v-else>{{ rowStatus(history) | customStatusLabel | uppercase }}</span>
                </td>
                <td
                  v-if="showCommentCol"
                  class="lifecycle-modal__comment"
                >
                  <div class="lifecycle-modal__comment-content">
                    <div
                      v-if="htmlCommentAttr && getCommentCellHtml(history)"
                      class="lifecycle-modal__comment-html"
                      v-html="getCommentCellHtml(history)"
                    />
                    <pre
                      v-else-if="allowPreformattedComments"
                    >{{ history.detail[commentAttr] }}</pre>
                    <template v-else>{{ history.detail[commentAttr] }}</template>
                    <template v-if="history.docs">
                      <pre>{{ $t('attachments') }}: <span v-if="history.docs && history.docs.length === 0"> {{ $t('na') }}</span> </pre>
                      <span
                        v-for="(file,index) in history.docs"
                        :key="index"
                      >
                        <a
                          @click="downloadfile(file)"
                        >{{ file.filename }}</a><br>
                      </span>
                    </template>
                  </div>
                </td>
                <td class="lifecycle-modal__username">{{ lifecycleWhoDisplay(history) }}</td>
                <td class="lifecycle-modal__date">{{ formatDate(rowCreatedAt(history)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        v-if="emptyHistoryHint && !histories.length"
        class="font-bold"
      >{{ emptyHistoryHint }}</div>
      <div class="row align-center nav_btn">
        <button
          type="button"
          class="btn btn--orange"
          data-close
          aria-label="Close reveal"
        >{{ $t('action.ok') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'

import * as xfs from '@/utils/xfs'
import dateUtil from '@/utils/dateUtil'
import userHelper from '@/utils/userHelper'
import { DIR_STATUS, DIR_ACTION } from '@/modules/directories/consts'
import { getParsedWorkPermitHistoryActor, getWorkPermitHistoryRowStatus } from '@/modules/workPermits/utils'

export default {
  name: 'Lifecycle',
  props: {
    modalId: {
      type: String,
      default: `modules-lifecycle-modal-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
    },
    type: {
      type: String,
      default: 'ITQ/Tender',
    },
    histories: {
      type: Array,
      default: () => [],
    },
    commentAttr: {
      type: String,
      default: 'rejectReason',
    },
    showStatusCol: {
      type: Boolean,
      default: true,
    },
    showCommentCol: {
      type: Boolean,
      default: true,
    },
    emptyHistoryHint: {
      type: String,
      default: '',
    },
    /**
     * Allow comments shows line breaks
     */
    allowPreformattedComments: {
      type: Boolean,
      default: false,
    },
    /**
     * author: alvis
     * timestamp: 2026-04-22
     * description: If set, use `history.detail[htmlCommentAttr]` (when non-empty) for v-html in the comment cell instead of plain pre/text (e.g. work permit merged strong labels).
     * parameter: prop name = htmlCommentAttr
     */
    htmlCommentAttr: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      userHelper,
      DIR_STATUS,
      DIR_ACTION,
    }
  },
  methods: {
    /**
     * author: alvis
     * timestamp: 2026-04-22
     * description: HTML fragment for a lifecycle history row’s comment cell when `htmlCommentAttr` is set.
     * @param {Object} history - History row (expects `detail`).
     * @returns {string} Safe HTML from `history.detail[htmlCommentAttr]`, or ''.
     */
    getCommentCellHtml (history) {
      if (!this.htmlCommentAttr || !history || !history.detail) {
        return ''
      }
      return history.detail[this.htmlCommentAttr] || ''
    },
    openModal () {
      this.lifecycleModal.open()
    },
    async init () {
      if (!this.lifecycleModal) {
        this.lifecycleModal = await new Foundation.Reveal($(`#${this.modalId}`), {
          closeOnEsc: false,
          closeOnClick: false,
        })
      }
      this.openModal()
    },
    /**
     * Sequelize list payloads often omit top-level `status` but set `dataValues.status`.
     * same as work permit WPCollapseContent.
     * @param {Object} history - A lifecycle / history row
     * @returns {string|undefined} Raw status for `customStatusLabel`
     */
    rowStatus (history) {
      return getWorkPermitHistoryRowStatus(history)
    },
    rowCreatedAt (history) {
      if (history == null) return null
      if (history.createdAt != null && history.createdAt !== '') {
        return history.createdAt
      }
      return lodash.get(history, 'dataValues.createdAt') || null
    },
    lifecycleWhoDisplay (history) {
      const w = getParsedWorkPermitHistoryActor(history)
      if (!w) {
        return ''
      }
      return userHelper.getUserName(w.username, w.displayName)
    },
    formatDate (dateString) {
      return dateUtil.formatDate(dateString, true)
    },
    downloadfile (file) {
      xfs.downloadFile(`/docs/${file.id}`)
    },
  },
  computed: {
    orderedItems () {
      return lodash.orderBy(
        this.histories,
        h => {
          const t = this.rowCreatedAt(h)
          return t ? new Date(t).getTime() : 0
        },
        'desc',
      )
    },
    title () {
      return this.$t(`lifecycle.${this.type}`)
    },
  },
  destroyed () {
    if (this.lifecycleModal) {
      this.lifecycleModal.destroy()
    }
    $(`#${this.modalId}`).remove()
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/table';
@import '~styles/popup';
</style>

<style lang="scss">
.lifecycle-modal {
  width: 1000px;
  max-width: calc(100vw - 48px);

  .flex-box{
    position: relative;
    box-sizing: border-box;
    padding: 0;
    max-height: calc( 100vh - 250px);
  }

 &__status {
    width: 1%;
    white-space: nowrap;
    word-break: normal;
  }
  &__comment {
    width: 99%;
    vertical-align: middle;
    word-break: break-word;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
  &__comment-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 48px;
  }
  &__username {
    width: 1%;
    white-space: nowrap;
    word-break: normal;
  }
  &__date {
    width: 1%;
    white-space: nowrap;
    word-break: normal;
  }
  .flex-box-content{
    max-height: calc( 100vh - 310px);
    overflow: auto;
    table {
      margin-bottom: 0;
      width: 100%;
      max-width: 100%;
      table-layout: auto;
    }

    a {
      text-decoration: underline;
    }
    .cart-icon {
      color: #e69500;
      font-size: 16px;
    }
  }
  .flex-box-footer{
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0 0 20px 0;
  }
  .font-bold {
    font-weight: bold;
  }
  pre{
    overflow: hidden;
  }
  .lifecycle-modal__comment-html {
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    overflow: hidden;

    .lifecycle-modal__comment-html-stack {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .lifecycle-modal__comment-html-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
    }

    .lifecycle-modal__comment-html-label {
      flex: 0 0 auto;
    }

    .lifecycle-modal__comment-html-text {
      flex: 1 1 auto;
      min-width: 0;
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .lifecycle-modal__comment-html-row--body-only .lifecycle-modal__comment-html-text {
      flex: 1 1 100%;
    }
  }
}

</style>
